// src/components/auth/QuartermasterLogin.tsx
// ✅ REDESIGN 2025-11-01: qmLEO branding update to match dashboard
// CHANGES: qmLEO logo, blue button (replacing green), brand colors

import React, { useState } from 'react';
import { Eye, EyeOff, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { fetchUserAttributes } from 'aws-amplify/auth';
import ForgotPasswordModal from './ForgotPasswordModal';
import qmLeoLogo from '../../assets/images/qmLEO_logo.png';

interface TenantInfo {
  tenantId: string;
  organizationName: string;
  tenantSlug: string;
}

interface LoginFormData {
  email: string;
  password: string;
}

interface QuartermasterLoginProps {
  tenant: TenantInfo;
  onLogin: (email: string, password: string, expectedTenantId: string) => Promise<boolean>;
  onLogout: () => Promise<void>;
  onChangeSite: () => void;
  error: string | null;
}

const QuartermasterLogin: React.FC<QuartermasterLoginProps> = ({ 
  tenant, 
  onLogin, 
  onLogout, 
  onChangeSite,
  error 
}) => {
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const handleLogin = async () => {
    if (!formData.email || !formData.password) {
      return;
    }

    setLoading(true);
    setLoginError(null);
    
    try {
      console.log('🔐 Attempting login for tenant:', tenant.organizationName);
      
      // Pass expected tenant ID to login function for validation
      const loginSuccess = await onLogin(formData.email, formData.password, tenant.tenantId);
      
      if (!loginSuccess) {
        console.error('❌ Login failed');
        setLoading(false);
        return;
      }

      // Validate user belongs to the correct tenant
      console.log('🔍 Validating tenant membership...');
      
      try {
        const userAttributes = await fetchUserAttributes();
        const userTenantId = userAttributes['custom:tenantId'] || userAttributes['custom:tenant_id'];
        
        console.log('👤 User tenant validation:', {
          expectedTenantId: tenant.tenantId,
          userTenantId,
          match: userTenantId === tenant.tenantId
        });

        if (userTenantId !== tenant.tenantId) {
          // USER BELONGS TO DIFFERENT TENANT - REJECT LOGIN
          console.error('❌ Tenant mismatch! User does not belong to this department.');
          setLoginError(
            `Your account is not associated with ${tenant.organizationName}. ` +
            `Please select the correct department or contact your administrator.`
          );
          
          // Log the user out immediately
          await onLogout();
          setLoading(false);
          return;
        }

        // SUCCESS - User belongs to the correct tenant
        console.log('✅ Tenant validation passed - Login successful');
        setSuccess(true);
        
      } catch (attrError) {
        console.error('❌ Failed to validate user attributes:', attrError);
        setLoginError('Failed to validate your account. Please try again.');
        await onLogout();
        setLoading(false);
        return;
      }
      
    } catch (err: any) {
      console.error('❌ Login error:', err);
      setLoginError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (loginError) {
      setLoginError(null);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && formData.email && formData.password) {
      handleLogin();
    }
  };

  const handleForgotPasswordSuccess = () => {
    console.log('✅ Password reset completed successfully');
    setShowForgotPassword(false);
  };

  // Determine which error to show
  const displayError = loginError || error;

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Header with qmLEO Logo */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <img 
                src={qmLeoLogo} 
                alt="qmLEO" 
                className="h-24 w-auto"
              />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              qmLEO
            </h1>
            <p className="text-gray-600">
              Secure Equipment Management
            </p>
          </div>

          {/* Main Login Card */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            {/* Department Header - PowerDMS Style */}
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900 truncate">
                {tenant.organizationName}
              </h2>
              <button
                onClick={onChangeSite}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors whitespace-nowrap ml-4"
                disabled={loading}
              >
                Change Site
              </button>
            </div>

            {/* Login Form */}
            <div className="p-8">
              <div className="space-y-4">
                {/* Email Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Username
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter your email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    required
                    autoComplete="email"
                    disabled={loading}
                    autoFocus
                  />
                </div>

                {/* Password Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder="Enter your password"
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      required
                      autoComplete="current-password"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 transition-colors"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      disabled={loading}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <div className="mt-2 text-right">
                    <button 
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-blue-600 hover:text-blue-800 transition-colors font-medium"
                      disabled={loading}
                    >
                      Forgot Password?
                    </button>
                  </div>
                </div>

                {/* Error Display */}
                {displayError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                      <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" />
                      <p className="text-sm text-red-800">{displayError}</p>
                    </div>
                  </div>
                )}

                {/* Success Display */}
                {success && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
                      <p className="text-sm text-green-800">Login successful! Loading dashboard...</p>
                    </div>
                  </div>
                )}

                {/* Submit Button - Blue to match brand */}
                <button
                  onClick={handleLogin}
                  disabled={loading || success || !formData.email || !formData.password}
                  className="w-full bg-blue-600 hover:bg-blue-700 py-3 px-4 rounded-lg text-white font-medium transition-all duration-200 flex items-center justify-center shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Signing In...
                    </>
                  ) : success ? (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Success!
                    </>
                  ) : (
                    'Login'
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Need access? Contact your department administrator
            </p>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        onSuccess={handleForgotPasswordSuccess}
      />
    </>
  );
};

export default QuartermasterLogin;
